import { useEffect, useCallback } from 'react';
import { isBrowser } from '@belong/env';

const FREQUENCY = 800;
const RAMP_VALUE = 0.00001;
const RAMP_DURATION = 1;

interface IUseBeepNotificationOptions {
  frequency?: number;
  interval?: number;
}

interface IUseBeepNotification {
  playBeep: () => void;
}

// Keep one audioContext and reuse it, with reference counting
let audioContext: AudioContext | undefined;
let activeHooks = 0;

const createAudioContext = (): AudioContext | undefined => {
  if (!audioContext && isBrowser() && window.AudioContext) {
    audioContext = new window.AudioContext();
  }
  return audioContext;
};

const useAudioContext = (): AudioContext | undefined => {
  const destroyContext = useCallback((): void => {
    if (audioContext && activeHooks === 0) {
      audioContext.close();
      audioContext = undefined;
    }
  }, []);

  useEffect(() => {
    activeHooks += 1;
    return () => {
      activeHooks -= 1;
      destroyContext();
    };
  }, [destroyContext]);

  return createAudioContext();
};

export const useBeepNotification = (options?: IUseBeepNotificationOptions): IUseBeepNotification => {
  const frequency = options?.frequency || FREQUENCY;
  const context = useAudioContext();

  const playBeep = useCallback((): void => {
    if (!isBrowser() || !context) {
      return;
    }

    const { currentTime } = context;
    const osc = context.createOscillator();
    const gain = context.createGain();

    osc.connect(gain);
    gain.connect(context.destination);

    gain.gain.setValueAtTime(gain.gain.value, currentTime);
    gain.gain.exponentialRampToValueAtTime(RAMP_VALUE, currentTime + RAMP_DURATION);

    osc.onended = (): void => {
      gain.disconnect(context.destination);
      osc.disconnect(gain);
    };

    osc.type = 'sine';
    osc.frequency.value = frequency;
    osc.start(currentTime);
    osc.stop(currentTime + RAMP_DURATION);
  }, [context, frequency]);

  return {
    playBeep
  };
};
